import { platformApi } from './api';
import type {
  StorageBucket,
  StorageObject,
  StorageObjectVersion,
  PresignedURL,
  StorageUsage,
  BucketIAMUser,
  BucketIAMAccessKey,
  BucketIAMRole,
  BucketOperation,
  CreatedAccessKey,
  StoragePlan,
  PlanRequest,
  PricingResponse,
  QuoteRequest,
  QuoteResponse,
} from '../types/objectStorage';

const h = (projectId: string) => ({ 'X-Project-Id': projectId });

export interface CompletedPart {
  part_number: number;
  etag: string;
}

export interface InitiateMultipartResponse {
  upload_id: string;
  key: string;
}

export interface UploadPartResponse {
  part_number: number;
  etag: string;
}

export const objectStorageService = {
  // Usage
  getUsage: (projectId: string): Promise<StorageUsage> =>
    platformApi
      .get<StorageUsage>('/object-storage/usage', { headers: h(projectId) })
      .then((r) => r.data),

  // Buckets
  listBuckets: (projectId: string): Promise<StorageBucket[]> =>
    platformApi
      .get<{ buckets: StorageBucket[] }>('/object-storage/buckets', { headers: h(projectId) })
      .then((r) => r.data.buckets ?? []),

  createBucket: (
    projectId: string,
    data: {
      name: string;
      region: string;
      versioning: boolean;
      is_public: boolean;
      plan_id?: string;           // legacy
      plan_type?: 'starter' | 'committed'; // new
      min_commitment_tb?: number;          // new (committed only)
    }
  ): Promise<StorageBucket> =>
    platformApi
      .post<{ bucket: StorageBucket }>('/object-storage/buckets', data, { headers: h(projectId) })
      .then((r) => r.data.bucket),

  listPlans: (projectId: string): Promise<StoragePlan[]> =>
    platformApi
      .get<{ plans: StoragePlan[] }>('/object-storage/plans', { headers: h(projectId) })
      .then((r) => r.data.plans ?? []),

  submitPlanRequest: (
    projectId: string,
    data: { email?: string; requested_gb: number; comments: string }
  ): Promise<PlanRequest> =>
    platformApi
      .post<{ plan_request: PlanRequest }>('/object-storage/plan-requests', data, { headers: h(projectId) })
      .then((r) => r.data.plan_request),

  getBucket: (projectId: string, bucketId: string): Promise<StorageBucket> =>
    platformApi
      .get<{ bucket: StorageBucket }>(`/object-storage/buckets/${bucketId}`, { headers: h(projectId) })
      .then((r) => r.data.bucket),

  updateBucket: (
    projectId: string,
    bucketId: string,
    data: { versioning: boolean; is_public: boolean }
  ): Promise<StorageBucket> =>
    platformApi
      .put<{ bucket: StorageBucket }>(`/object-storage/buckets/${bucketId}`, data, { headers: h(projectId) })
      .then((r) => r.data.bucket),

  deleteBucket: (projectId: string, bucketId: string): Promise<void> =>
    platformApi
      .delete(`/object-storage/buckets/${bucketId}`, { headers: h(projectId) })
      .then(() => undefined),

  // Objects
  listObjects: (
    projectId: string,
    bucketId: string,
    prefix?: string,
    recursive?: boolean
  ): Promise<StorageObject[]> =>
    platformApi
      .get<{ objects: StorageObject[] }>(`/object-storage/buckets/${bucketId}/objects`, {
        headers: h(projectId),
        params: { prefix, recursive },
      })
      .then((r) => r.data.objects ?? []),

  uploadObject: (
    projectId: string,
    bucketId: string,
    file: File,
    key: string,
    onProgress?: (pct: number) => void
  ): Promise<StorageObject> => {
    const form = new FormData();
    form.append('file', file);
    form.append('key', key);
    return platformApi
      .post<{ object: StorageObject }>(
        `/object-storage/buckets/${bucketId}/objects/upload`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data', 'X-Project-Id': projectId },
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
          },
        }
      )
      .then((r) => r.data.object);
  },

  deleteObject: (projectId: string, bucketId: string, key: string): Promise<void> =>
    platformApi
      .delete(`/object-storage/buckets/${bucketId}/objects`, {
        headers: h(projectId),
        params: { key },
      })
      .then(() => undefined),

  bulkDeleteObjects: (
    projectId: string,
    bucketId: string,
    keys: string[]
  ): Promise<{ deleted: string[]; failed: string[] }> =>
    platformApi
      .post<{ deleted: string[]; failed: string[] }>(
        `/object-storage/buckets/${bucketId}/objects/bulk-delete`,
        { keys },
        { headers: h(projectId) }
      )
      .then((r) => r.data),

  copyObject: (
    projectId: string,
    bucketId: string,
    src_key: string,
    dst_key: string
  ): Promise<void> =>
    platformApi
      .post(`/object-storage/buckets/${bucketId}/objects/copy`, { src_key, dst_key }, { headers: h(projectId) })
      .then(() => undefined),

  moveObject: (
    projectId: string,
    bucketId: string,
    src_key: string,
    dst_key: string
  ): Promise<void> =>
    platformApi
      .post(`/object-storage/buckets/${bucketId}/objects/move`, { src_key, dst_key }, { headers: h(projectId) })
      .then(() => undefined),

  getObjectMetadata: (
    projectId: string,
    bucketId: string,
    key: string
  ): Promise<Record<string, string>> =>
    platformApi
      .get<{ metadata: Record<string, string> }>(`/object-storage/buckets/${bucketId}/objects/metadata`, {
        headers: h(projectId),
        params: { key },
      })
      .then((r) => r.data.metadata),

  setObjectMetadata: (
    projectId: string,
    bucketId: string,
    key: string,
    metadata: Record<string, string>
  ): Promise<void> =>
    platformApi
      .put(`/object-storage/buckets/${bucketId}/objects/metadata`, { key, metadata }, { headers: h(projectId) })
      .then(() => undefined),

  createFolder: (projectId: string, bucketId: string, prefix: string): Promise<void> =>
    platformApi
      .post(`/object-storage/buckets/${bucketId}/folders`, { prefix }, { headers: h(projectId) })
      .then(() => undefined),

  // Versioning
  listObjectVersions: (
    projectId: string,
    bucketId: string,
    key: string
  ): Promise<StorageObjectVersion[]> =>
    platformApi
      .get<{ versions: StorageObjectVersion[] }>(
        `/object-storage/buckets/${bucketId}/objects/versions`,
        { headers: h(projectId), params: { key } }
      )
      .then((r) => r.data.versions ?? []),

  restoreObjectVersion: (
    projectId: string,
    bucketId: string,
    key: string,
    version_id: string
  ): Promise<void> =>
    platformApi
      .post(
        `/object-storage/buckets/${bucketId}/objects/restore`,
        { key, version_id },
        { headers: h(projectId) }
      )
      .then(() => undefined),

  deleteObjectVersion: (
    projectId: string,
    bucketId: string,
    key: string,
    version_id: string
  ): Promise<void> =>
    platformApi
      .delete(`/object-storage/buckets/${bucketId}/objects/versions`, {
        headers: h(projectId),
        params: { key, version_id },
      })
      .then(() => undefined),

  // Pre-authenticated URLs
  presignObject: (
    projectId: string,
    bucketId: string,
    key: string,
    operation: 'get' | 'put',
    expiry_minutes = 60
  ): Promise<PresignedURL> =>
    platformApi
      .post<PresignedURL>(
        `/object-storage/buckets/${bucketId}/presign`,
        { key, operation, expiry_minutes },
        { headers: h(projectId) }
      )
      .then((r) => r.data),

  // IAM
  listBucketUsers: (projectId: string, bucketId: string): Promise<BucketIAMUser[]> =>
    platformApi
      .get<{ users: BucketIAMUser[] }>(`/object-storage/buckets/${bucketId}/iam/users`, { headers: h(projectId) })
      .then((r) => r.data.users ?? []),

  createBucketUser: (
    projectId: string,
    bucketId: string,
    username: string,
    operations: BucketOperation[]
  ): Promise<{ user: BucketIAMUser; access_key: CreatedAccessKey }> =>
    platformApi
      .post<{ user: BucketIAMUser; access_key: CreatedAccessKey }>(
        `/object-storage/buckets/${bucketId}/iam/users`,
        { username, operations },
        { headers: h(projectId) }
      )
      .then((r) => r.data),

  deleteBucketUser: (projectId: string, bucketId: string, username: string): Promise<void> =>
    platformApi
      .delete(`/object-storage/buckets/${bucketId}/iam/users/${encodeURIComponent(username)}`, {
        headers: h(projectId),
      })
      .then(() => undefined),

  listUserAccessKeys: (projectId: string, bucketId: string, username: string): Promise<BucketIAMAccessKey[]> =>
    platformApi
      .get<{ access_keys: BucketIAMAccessKey[] }>(
        `/object-storage/buckets/${bucketId}/iam/users/${encodeURIComponent(username)}/keys`,
        { headers: h(projectId) }
      )
      .then((r) => r.data.access_keys ?? []),

  createUserAccessKey: (projectId: string, bucketId: string, username: string): Promise<CreatedAccessKey> =>
    platformApi
      .post<{ access_key: CreatedAccessKey }>(
        `/object-storage/buckets/${bucketId}/iam/users/${encodeURIComponent(username)}/keys`,
        {},
        { headers: h(projectId) }
      )
      .then((r) => r.data.access_key),

  deleteUserAccessKey: (
    projectId: string,
    bucketId: string,
    username: string,
    accessKeyId: string
  ): Promise<void> =>
    platformApi
      .delete(
        `/object-storage/buckets/${bucketId}/iam/users/${encodeURIComponent(username)}/keys/${encodeURIComponent(accessKeyId)}`,
        { headers: h(projectId) }
      )
      .then(() => undefined),

  listBucketRoles: (projectId: string, bucketId: string): Promise<BucketIAMRole[]> =>
    platformApi
      .get<{ roles: BucketIAMRole[] }>(`/object-storage/buckets/${bucketId}/iam/roles`, { headers: h(projectId) })
      .then((r) => r.data.roles ?? []),

  createBucketRole: (
    projectId: string,
    bucketId: string,
    roleName: string,
    operations: BucketOperation[]
  ): Promise<BucketIAMRole> =>
    platformApi
      .post<{ role: BucketIAMRole }>(
        `/object-storage/buckets/${bucketId}/iam/roles`,
        { role_name: roleName, operations },
        { headers: h(projectId) }
      )
      .then((r) => r.data.role),

  deleteBucketRole: (projectId: string, bucketId: string, roleName: string): Promise<void> =>
    platformApi
      .delete(`/object-storage/buckets/${bucketId}/iam/roles/${encodeURIComponent(roleName)}`, {
        headers: h(projectId),
      })
      .then(() => undefined),

  // Multipart upload
  initiateMultipart: (
    projectId: string,
    bucketId: string,
    key: string,
    contentType: string,
  ): Promise<InitiateMultipartResponse> =>
    platformApi
      .post<InitiateMultipartResponse>(
        `/object-storage/buckets/${bucketId}/objects/multipart`,
        { key, content_type: contentType },
        { headers: h(projectId) },
      )
      .then((r) => r.data),

  uploadPart: (
    projectId: string,
    bucketId: string,
    key: string,
    uploadId: string,
    partNumber: number,
    blob: Blob,
    onProgress?: (loaded: number, total: number) => void,
    signal?: AbortSignal,
  ): Promise<UploadPartResponse> =>
    platformApi
      .put<UploadPartResponse>(
        `/object-storage/buckets/${bucketId}/objects/multipart/${uploadId}/parts/${partNumber}?key=${encodeURIComponent(key)}`,
        blob,
        {
          headers: {
            ...h(projectId),
            'Content-Type': 'application/octet-stream',
          },
          signal,
          timeout: 180_000,
          onUploadProgress: (e) => {
            if (onProgress) onProgress(e.loaded, e.total ?? blob.size);
          },
        },
      )
      .then((r) => r.data),

  completeMultipart: (
    projectId: string,
    bucketId: string,
    key: string,
    uploadId: string,
    parts: CompletedPart[],
    totalSize: number,
  ): Promise<{ object: StorageObject }> =>
    platformApi
      .post<{ object: StorageObject }>(
        `/object-storage/buckets/${bucketId}/objects/multipart/${uploadId}/complete`,
        { key, parts, total_size: totalSize },
        { headers: h(projectId) },
      )
      .then((r) => r.data),

  abortMultipart: (
    projectId: string,
    bucketId: string,
    key: string,
    uploadId: string,
  ): Promise<void> =>
    platformApi
      .delete(
        `/object-storage/buckets/${bucketId}/objects/multipart/${uploadId}?key=${encodeURIComponent(key)}`,
        { headers: h(projectId) },
      )
      .then(() => undefined),

  // Pricing
  getPricing: async (projectId: string): Promise<PricingResponse> => {
    const { data } = await platformApi.get(
      `/object-storage/pricing`,
      { headers: h(projectId) },
    );
    return data;
  },

  getQuote: async (projectId: string, req: QuoteRequest): Promise<QuoteResponse> => {
    const params = new URLSearchParams({
      bytes: String(req.bytes),
      plan_type: req.plan_type,
    });
    if (req.commitment_tb !== undefined) {
      params.set('commitment_tb', String(req.commitment_tb));
    }
    const { data } = await platformApi.get(
      `/object-storage/quote?${params.toString()}`,
      { headers: h(projectId) },
    );
    return data;
  },

};
